/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { CommandBarButton, IButtonProps, IButtonStyles } from '@fluentui/react/lib/Button';
import { ICommandBarItemProps } from '@fluentui/react/lib/CommandBar';
import { IContextualMenuProps } from '@fluentui/react/lib/ContextualMenu';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisCommandBar } from 'src/core/components/LuisCommandBar';
import { LuisDebouncedSearchBox } from 'src/core/components/LuisDebouncedSearchBox';
import { RBACTooltip } from 'src/core/components/RBACTooltip';
import { TableCommandBarProps } from 'src/core/components/TableCommandBarProps';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { FileType } from 'src/core/types/FileType';
import { getCommandBarButtonStyles } from 'src/core/utils/azureRbacStyles';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { commandBarItemSelectionManager } from 'src/core/utils/commandBarItemSelectionManager';
import { LuisAppStore } from 'src/luis/core/stores/LuisAppStore';
import { PublishSlotType } from 'src/luis/types/PublishSlotType';

export type ConversationAppsTableCommandBarProps = TableCommandBarProps<LuisAppStore> & {
    /**
     * Indicates if user can create an app.
     */
    canCreateApp: boolean;

    /**
     * Callback for renaming an app.
     */
    onRenameApp: () => boolean | void;

    /**
     * Callback for deleting an app.
     */
    onDeleteApps: () => boolean | void;

    /**
     * Callback for creating a new conversation app.
     */
    onCreateApp: () => void;

    /**
     * Callback for importing a conversation app.
     */
    onImportApp: (fileType: FileType) => boolean | void;

    /**
     * Callback for importing an app.
     */
    onImportLogs: () => boolean | void;

    /**
     * Callback for exporting an app.
     */
    onExportApp: (fileType: FileType) => boolean | void;

    /**
     * Callback for migrating apps.
     */
    onMigrateApp: () => boolean | void;

    /**
     * Callback for exporting an app for container.
     */
    onExportAppForContainer: (slot: PublishSlotType) => boolean | void;

    /**
     * Callback for exporting an app logs.
     */
    onExportLogs: () => boolean | void;

    /**
     * User role to determine the actions
     */
    userRole: string;

    /**
     * User role to show in LuisTooltip message
     */
    displayedUserRole: string;
};

const messages = defineMessages({
    searchPlaceholder: {
        id: 'luis.conversationAppsTableCommandBar.searchPlaceholder',
        defaultMessage: 'Search apps ...'
    },
    createAppOption: {
        id: 'luis.conversationAppsTableCommandBar.createAppOption',
        defaultMessage: 'New app'
    },
    createAppOptionAriaLabel: {
        id: 'luis.conversationAppsTableCommandBar.createAppOptionAriaLabel',
        defaultMessage: 'Create New app'
    },
    importAppJsonButton: {
        id: 'luis.conversationAppsTableCommandBar.importAppJsonButton',
        defaultMessage: 'Import as JSON'
    },
    importAppLuButton: {
        id: 'luis.conversationAppsTableCommandBar.importAppLuButton',
        defaultMessage: 'Import as LU'
    },
    renameAppButton: {
        id: 'luis.conversationAppsTableCommandBar.renameAppButton',
        defaultMessage: 'Rename'
    },
    deleteAppButton: {
        id: 'luis.conversationAppsTableCommandBar.deleteAppButton',
        defaultMessage: 'Delete'
    },
    migrateAppButton: {
        id: 'luis.conversationAppsTableCommandBar.migrateAppButton',
        defaultMessage: 'Migrate'
    },
    importLogsButton: {
        id: 'luis.conversationAppsTableCommandBar.importLogsButton',
        defaultMessage: 'Import logs'
    },
    exportLogsButton: {
        id: 'luis.conversationAppsTableCommandBar.exportLogsButton',
        defaultMessage: 'Export logs'
    },
    exportAppButton: {
        id: 'luis.conversationAppsTableCommandBar.exportAppButton',
        defaultMessage: 'Export'
    },
    exportAppButtonAriaLabel: {
        id: 'luis.conversationAppsTableCommandBar.exportAppButtonAriaLabel',
        defaultMessage: 'Export app'
    },
    exportAppJsonButton: {
        id: 'luis.conversationAppsTableCommandBar.exportAppJsonButton',
        defaultMessage: 'Export as JSON'
    },
    exportAppLuButton: {
        id: 'luis.conversationAppsTableCommandBar.exportAppLuButton',
        defaultMessage: 'Export as LU'
    },
    exportAppForContainerButton: {
        id: 'luis.conversationAppsTableCommandBar.exportAppForContainerButton',
        defaultMessage: 'Export for Container'
    },
    exportAppProductionSlotButton: {
        id: 'luis.conversationAppsTableCommandBar.exportAppProductionSlotButton',
        defaultMessage: 'Production Slot'
    },
    exportAppStagingSlotButton: {
        id: 'luis.conversationAppsTableCommandBar.exportAppStagingSlotButton',
        defaultMessage: 'Staging Slot'
    },
    canNotCreateAppMessage: {
        id: 'luis.conversationAppsTableCommandBar.canNotCreateAppMessage',
        defaultMessage: 'Please select a subscription and an authoring resource first'
    },
    import: {
        id: 'luis.conversationAppsTableCommandBar.import',
        defaultMessage: 'Import'
    },
    importButtonAriaLabel: {
        id: 'luis.conversationAppsTableCommandBar.importButtonAriaLabel',
        defaultMessage: 'Import App'
    }
});

const addConversationAppButtonStyles = classNamesFunction<IButtonProps, IButtonStyles>()({
    menuIcon: { lineHeight: '20px' },
    rootDisabled: { backgroundColor: 'transparent' },
    label: { fontWeight: 'normal', whiteSpace: 'nowrap' },
    root: { backgroundColor: 'transparent', border: 'none', height: '100%', padding: '0 4px', minWidth: 'auto' }
});

/**
 * Represents a command bar for the conversation applications table.
 *
 * @param props The properties for the applications table command bar.
 */
export const ConversationAppsTableCommandBar = (props: ConversationAppsTableCommandBarProps) => {
    const { selectedItems = [], canCreateApp, query = '', onQueryChange, userRole, displayedUserRole } = props;
    const { canWrite, canDeleteProject, canChangeAppSettingsAndDetails } = mapUserRoleToUserActions(userRole);
    const { localize } = useLocalization();

    const luisCExportSubMenuItems: IContextualMenuProps = {
        items: [
            { key: 'exportJson', name: localize(messages.exportAppJsonButton), onClick: () => props.onExportApp('json') },
            { key: 'exportLu', name: localize(messages.exportAppLuButton), onClick: () => props.onExportApp('lu') },
            {
                key: 'exportForContainer',
                name: localize(messages.exportAppForContainerButton),
                subMenuProps: {
                    items: [
                        {
                            key: 'productionSlot',
                            name: localize(messages.exportAppProductionSlotButton),
                            onClick: () => props.onExportAppForContainer('production'),
                            disabled: !selectedItems[0]?.publishSlots?.production
                        },
                        {
                            key: 'stagingSlot',
                            name: localize(messages.exportAppStagingSlotButton),
                            onClick: () => props.onExportAppForContainer('staging'),
                            disabled: !selectedItems[0]?.publishSlots?.staging
                        }
                    ]
                }
            }
        ]
    };

    const commands: ICommandBarItemProps[] = [
        {
            key: 'conversation',
            onRender: (item: ICommandBarItemProps) => (
                <RBACTooltip
                    displayedUserRole={displayedUserRole}
                    hidden={(canWrite && canCreateApp) || item.disabled}
                    data-automation-id="newConversationAppButtonLuisToolTip"
                    content={!canCreateApp && localize(messages.canNotCreateAppMessage)}
                    componentSurroundedByToolTip={
                        <CommandBarButton
                            onClick={props.onCreateApp}
                            iconProps={{ iconName: 'Add' }}
                            styles={addConversationAppButtonStyles}
                            text={localize(messages.createAppOption)}
                            data-automation-id="newConversationAppButton"
                            ariaLabel={localize(messages.createAppOptionAriaLabel)}
                            disabled={!canCreateApp || !canWrite || item.disabled}
                        />
                    }
                />
            )
        },
        {
            key: 'import',
            onRender: (item: ICommandBarItemProps) => (
                <RBACTooltip
                    displayedUserRole={displayedUserRole}
                    hidden={(canWrite && canCreateApp) || item.disabled}
                    data-automation-id="importConversationAppButtonLuisToolTip"
                    content={!canCreateApp && localize(messages.canNotCreateAppMessage)}
                    componentSurroundedByToolTip={
                        <CommandBarButton
                            text={localize(messages.import)}
                            iconProps={{ iconName: 'Upload' }}
                            ariaLabel={localize(messages.importButtonAriaLabel)}
                            disabled={!canCreateApp || !canWrite || item.disabled}
                            menuProps={{
                                items: [
                                    {
                                        key: 'importJson',
                                        onClick: () => props.onImportApp('json'),
                                        iconProps: { iconName: 'Upload' },
                                        name: localize(messages.importAppJsonButton)
                                    },
                                    {
                                        key: 'importLu',
                                        iconProps: { iconName: 'Upload' },
                                        onClick: () => props.onImportApp('lu'),
                                        name: localize(messages.importAppLuButton)
                                    }
                                ]
                            }}
                            styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                        />
                    }
                />
            )
        },
        {
            key: 'export',
            iconProps: { iconName: 'Download' },
            subMenuProps: luisCExportSubMenuItems,
            name: localize(messages.exportAppButton),
            ariaLabel: localize(messages.exportAppButtonAriaLabel)
        },
        {
            key: 'importLogs',
            onRender: (item: ICommandBarItemProps) => (
                <RBACTooltip
                    hidden={canWrite || item.disabled}
                    displayedUserRole={displayedUserRole}
                    data-automation-id="importAppButtonLuisToolTip"
                    componentSurroundedByToolTip={
                        <CommandBarButton
                            onClick={props.onImportLogs}
                            iconProps={{ iconName: 'Upload' }}
                            data-automation-id="importAppButton"
                            disabled={!canWrite || item.disabled}
                            text={localize(messages.importLogsButton)}
                            name={localize(messages.importLogsButton)}
                            ariaLabel={localize(messages.importLogsButton)}
                            styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                        />
                    }
                />
            )
        },
        {
            key: 'exportLogs',
            onClick: props.onExportLogs,
            iconProps: { iconName: 'Download' },
            name: localize(messages.exportLogsButton),
            ariaLabel: localize(messages.exportLogsButton)
        },
        {
            key: 'rename',
            onRender: (item: ICommandBarItemProps) => (
                <RBACTooltip
                    hidden={canChangeAppSettingsAndDetails || item.disabled}
                    displayedUserRole={displayedUserRole}
                    data-automation-id="renameAppButtonLuisToolTip"
                    componentSurroundedByToolTip={
                        <CommandBarButton
                            onClick={props.onRenameApp}
                            iconProps={{ iconName: 'Rename' }}
                            data-automation-id="renameAppButton"
                            text={localize(messages.renameAppButton)}
                            name={localize(messages.renameAppButton)}
                            ariaLabel={localize(messages.renameAppButton)}
                            styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                            disabled={!canChangeAppSettingsAndDetails || item.disabled}
                        />
                    }
                />
            )
        },
        {
            key: 'delete',
            onRender: (item: ICommandBarItemProps) => (
                <RBACTooltip
                    hidden={canDeleteProject || item.disabled}
                    displayedUserRole={displayedUserRole}
                    data-automation-id="deleteAppButtonLuisToolTip"
                    componentSurroundedByToolTip={
                        <CommandBarButton
                            onClick={props.onDeleteApps}
                            iconProps={{ iconName: 'Delete' }}
                            data-automation-id="deleteAppButton"
                            text={localize(messages.deleteAppButton)}
                            name={localize(messages.deleteAppButton)}
                            disabled={!canDeleteProject || item.disabled}
                            ariaLabel={localize(messages.deleteAppButton)}
                            styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                        />
                    }
                />
            )
        },
        {
            key: 'migrate',
            onRender: (item: ICommandBarItemProps) => (
                <RBACTooltip
                    hidden={canChangeAppSettingsAndDetails || item.disabled}
                    displayedUserRole={displayedUserRole}
                    data-automation-id="migrateAppButtonLuisToolTip"
                    componentSurroundedByToolTip={
                        <CommandBarButton
                            onClick={props.onMigrateApp}
                            iconProps={{ iconName: 'Rename' }}
                            data-automation-id="migrateAppButton"
                            text={localize(messages.migrateAppButton)}
                            name={localize(messages.migrateAppButton)}
                            ariaLabel={localize(messages.migrateAppButton)}
                            styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                            disabled={!canChangeAppSettingsAndDetails || item.disabled}
                        />
                    }
                />
            )
        }
    ];

    const items: ICommandBarItemProps[] = commandBarItemSelectionManager(
        commands,
        ['conversation', 'import'],
        ['rename', 'export', 'importLogs', 'exportLogs', 'delete', 'migrate'],
        ['delete', 'migrate'],
        selectedItems.length
    );

    const farItems: ICommandBarItemProps[] = [
        {
            key: 'searchBox',
            onRender: () => (
                <LuisDebouncedSearchBox
                    size={36}
                    value={query}
                    ariaLabel={localize(messages.searchPlaceholder)}
                    placeholder={localize(messages.searchPlaceholder)}
                    onChange={(_, newValue?: string) => onQueryChange(newValue)}
                />
            )
        }
    ];

    return <LuisCommandBar items={items} farItems={farItems} />;
};
